// ✅ ListeColis.jsx avec correction du scroll bloqué
import React, { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import {
    collection,
    onSnapshot,
    updateDoc,
    doc,
    query,
    orderBy,
    deleteDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import jsPDF from "jspdf";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ListeColis() {
    const [colisList, setColisList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedColis, setSelectedColis] = useState(null);
    const [nextStep, setNextStep] = useState("");
    const [file, setFile] = useState(null);
    const [viewMode, setViewMode] = useState("card");
    const [slideImage, setSlideImage] = useState(null);
    const [colisASupprimer, setColisASupprimer] = useState(null);
    const [stepDate, setStepDate] = useState("");
    const [stepArticles, setStepArticles] = useState("");
    const [editColis, setEditColis] = useState(null);
    const [editForm, setEditForm] = useState({});

    const ETAPES = [
        { key: "commande", label: "Commande" },
        { key: "entrepot", label: "Entrepôt" },
        { key: "conteneur", label: "Conteneur" },
        { key: "recu", label: "Reçu" }
    ];

    const restoreScroll = () => {
        if (!document.querySelector(".modal.show")) {
            document.body.classList.remove("modal-open");
            document.body.style = "";
        }
    };

    useEffect(() => {
        const handleModalFix = () => {
            const anyModalOpen = document.querySelector(".modal.show");
            if (!anyModalOpen && document.body.classList.contains("modal-open")) {
                document.body.classList.remove("modal-open");
                document.body.style = "";
            }
        };

        const observer = new MutationObserver(handleModalFix);
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["class"]
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const q = query(collection(db, "colis"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setColisList(list);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const openModalForStatut = (colis) => {
        const current = colis.statut || "commande";
        const step = {
            commande: "entrepot",
            entrepot: "conteneur",
            conteneur: "recu"
        }[current];
        if (!step) return;
        setSelectedColis(colis);
        setNextStep(step);
        const modal = new window.bootstrap.Modal(document.getElementById("preuveModal"));
        modal.show();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
            toast.error("Seules les images JPG et PNG sont acceptées.");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Fichier trop volumineux (max 2 Mo).");
            return;
        }
        setFile(file);
    };

    const handleUploadPreuve = async () => {
        if (!selectedColis || !file) return;
        const articlesKey = {
            entrepot: "articlesEntrepot",
            conteneur: "articlesConteneur",
            recu: "articlesLivres"
        }[nextStep];

        const dejaTraites = selectedColis[articlesKey] || 0;
        const totalCommandes = selectedColis.nombreArticles || 0;
        const aAjouter = Number(stepArticles);

        if (dejaTraites + aAjouter > totalCommandes) {
            toast.error("Impossible de dépasser le nombre total d'articles commandés.");
            return;
        }

        const storageRef = ref(storage, `preuves/${selectedColis.id}_${nextStep}_${Date.now()}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        const refColis = doc(db, "colis", selectedColis.id);
        const preuveKey = `preuve_${nextStep}`;
        const dateKey = `date_${nextStep}`;
        const dateValue = stepDate ? new Date(stepDate).toISOString() : new Date().toISOString();

        await updateDoc(refColis, {
            statut: nextStep,
            [preuveKey]: downloadURL,
            [dateKey]: dateValue,
            [articlesKey]: dejaTraites + aAjouter
        });

        setFile(null);
        setStepDate("");
        setStepArticles("");
        const modal = window.bootstrap.Modal.getInstance(document.getElementById("preuveModal"));
        modal.hide();
        restoreScroll();
    };

    const confirmerSuppression = async () => {
        if (!colisASupprimer) return;
        try {
            await deleteDoc(doc(db, "colis", colisASupprimer.id));
            toast.success("Colis supprimé avec succès !");
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la suppression.");
        }
        setColisASupprimer(null);
        const modal = window.bootstrap.Modal.getInstance(document.getElementById("confirmDeleteModal"));
        modal.hide();
        restoreScroll();
    };

    const openEditModal = (colis) => {
        setEditColis(colis);
        setEditForm({ ...colis });
        const modal = new window.bootstrap.Modal(document.getElementById("editColisModal"));
        modal.show();
    };

    const imprimerRecu = (colis) => {
        const docPDF = new jsPDF();
        docPDF.setFontSize(16);
        docPDF.text("\ud83d\udcc4 Reçu de livraison", 20, 20);
        docPDF.setFontSize(12);
        docPDF.text(`\ud83d\udce6 Colis : ${colis.colisNom}`, 20, 40);
        docPDF.text(`Conteneur : ${colis.conteneur || "-"}`, 20, 48);
        docPDF.text(`Date : ${colis.dateReception}`, 20, 56);
        docPDF.text(`Expéditeur : ${colis.envoyeurNom}`, 20, 72);
        docPDF.text(`Tél : ${colis.envoyeurTel}`, 20, 80);
        docPDF.text(`Récepteur : ${colis.recepteurNom}`, 20, 96);
        docPDF.text(`Tél : ${colis.recepteurTel}`, 20, 104);
        docPDF.setFontSize(10);
        docPDF.text("Reçu généré automatiquement.", 20, 280);
        docPDF.autoPrint();
        window.open(docPDF.output("bloburl"), "_blank");
    };
    // 🔧 Renvoie un badge en fonction du statut
const getBadge = (statut) => {
    switch (statut) {
        case "commande":
            return "Commande placée";
        case "entrepot":
            return "Livré à l'entrepôt";
        case "conteneur":
            return "Mis dans le conteneur";
        case "recu":
            return "Reçu";
        default:
            return "Inconnu";
    }
};

// ✅ Vérifie si une étape est atteinte
const isEtapeActive = (colis, etapeKey) => {
    const ordre = ["commande", "entrepot", "conteneur", "recu"];
    const currentIndex = ordre.indexOf(colis.statut || "commande");
    const etapeIndex = ordre.indexOf(etapeKey);
    return etapeIndex <= currentIndex;
};

// ✅ Affiche les preuves uploadées pour chaque étape
const renderPreuves = (colis) => {
    const preuves = ["entrepot", "conteneur", "recu"].map((etape) => {
        const url = colis[`preuve_${etape}`];
        const label = getBadge(etape);
        return url ? (
            <a key={etape} href={url} target="_blank" rel="noopener noreferrer" className="d-block text-decoration-none text-success small mb-1">
                📎 {label}
            </a>
        ) : null;
    });

    return preuves.length > 0 ? preuves : <span className="text-muted">Aucune</span>;
};


    // Le reste du JSX est à inclure ici
    if (loading) return <p>Chargement...</p>;

    return (
        <div className="d-flex flex-column flex-lg-row">
            <Sidebar />
            <div className="flex-grow-1 p-3" id="mainContent" style={{ marginLeft: "250px" }}>
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                        <h2>📋 Liste des colis enregistrés</h2>
                        <div className="d-flex flex-wrap gap-2">
                            <Link to="/admin/ReceptionColis" className="btn btn-success">
                                ➕ Ajouter un colis
                            </Link>
                            <button className={`btn btn-sm ${viewMode === "row" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setViewMode("row")}>Vue Liste</button>
                            <button className={`btn btn-sm ${viewMode === "card" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setViewMode("card")}>Vue Carte</button>
                        </div>
                    </div>

                    {colisList.length === 0 ? (
                        <p>Aucun colis disponible.</p>
                    ) : viewMode === "row" ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Colis</th>
                                        <th>Conteneur</th>
                                        <th>Date</th>
                                        <th>Expéditeur</th>
                                        <th>Récepteur</th>
                                        <th>Images</th>
                                        <th>Étape</th>
                                        <th>Preuves</th>
                                        <th>Reçu</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {colisList.map((colis) => (
                                        <tr key={colis.id}>
                                            <td>{colis.colisNom}</td>
                                            <td>{colis.conteneur || "-"}</td>
                                            <td>
                                                {colis.dateAchat
                                                    ? new Date(colis.dateAchat).toLocaleDateString()
                                                    : colis.dateReception
                                                        ? new Date(colis.dateReception).toLocaleDateString()
                                                        : "-"}
                                            </td>
                                            <td>
                                                <strong>{colis.envoyeurNom}</strong><br />
                                                {colis.envoyeurTel}<br />
                                                <small>{colis.envoyeurAdresse}</small>
                                            </td>
                                            <td>
                                                <strong>{colis.recepteurNom}</strong><br />
                                                {colis.recepteurTel}<br />
                                                <small>{colis.recepteurAdresse}</small>
                                            </td>
                                            <td>
                                                {colis.imageUrls?.length > 0 ? (
                                                    <div className="d-flex flex-nowrap overflow-auto gap-2">
                                                        {colis.imageUrls.map((img, i) => (
                                                            <img key={i} src={img} alt="img" width={60} className="img-thumbnail flex-shrink-0" />

                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">Aucune</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className="badge bg-info mb-2">
                                                    {getBadge(colis.statut || "commande")}
                                                </span>
                                                <div className="d-flex flex-column gap-1">
                                                    {ETAPES.map(etape => (
                                                        <span
                                                            key={etape.key}
                                                            className={`badge mb-1 ${isEtapeActive(colis, etape.key) ? "bg-success" : "bg-secondary"}`}
                                                            style={{ opacity: isEtapeActive(colis, etape.key) ? 1 : 0.5 }}
                                                        >
                                                            {etape.label}
                                                            {isEtapeActive(colis, etape.key) && colis[`date_${etape.key}`] ? (
                                                                <span className="ms-2 small">{new Date(colis[`date_${etape.key}`]).toLocaleDateString()}</span>
                                                            ) : null}
                                                        </span>
                                                    ))}
                                                </div>
                                                {colis.statut !== "recu" && (
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => openModalForStatut(colis)}
                                                    >
                                                        ➕ Étape suivante
                                                    </button>
                                                )}
                                            </td>
                                            <td>{renderPreuves(colis)}</td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-secondary" onClick={() => imprimerRecu(colis)}>
                                                    🖨️ Imprimer
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => openEditModal(colis)}
                                                >
                                                    ✏️ Modifier
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3">
                            {colisList.map((colis) => (
                                <div className="col" key={colis.id}>
                                    <div className="card h-100 shadow-sm">
                                        {colis.imageUrls?.[0] && (
                                            <img
  src={colis.imageUrls[0]}
  className="card-img-top bg-white"
  alt="colis"
  style={{
    height: "550px",
    width: "100%",
    objectFit: "contain",
    borderTopLeftRadius: "0.375rem",
    borderTopRightRadius: "0.375rem"
  }}
/>



                                        )}
                                        <div className="card-body d-flex flex-column justify-content-between">
                                            <div>
                                                <h6 className="card-title mb-2">📦 {colis.colisNom}</h6>
                                                <p className="card-text small mb-2">
                                                    <strong>Date :</strong>{" "}
                                                    {colis.dateAchat
                                                        ? new Date(colis.dateAchat).toLocaleDateString()
                                                        : colis.dateReception
                                                            ? new Date(colis.dateReception).toLocaleDateString()
                                                            : "-"}
                                                    <br />
                                                    <strong>Commandé :</strong> {colis.nombreArticles || "-"}<br />
                                                    <strong>Entrepôt :</strong> {colis.articlesEntrepot || 0}<br />
                                                    <strong>Conteneur :</strong> {colis.articlesConteneur || 0}<br />
                                                    <strong>Livré :</strong> {colis.articlesLivres || 0}<br />
                                                    <strong>Exp :</strong> {colis.envoyeurNom}<br />
                                                    <strong>Rec :</strong> {colis.recepteurNom}
                                                </p>
                                                {/* <span className="badge bg-info d-block mb-3">{getBadge(colis.statut)}</span> */}
                                                
                                                <div className="mb-2">
                                                    {ETAPES.map(etape => (
                                                        <span
                                                            key={etape.key}
                                                            className={`badge me-1 mb-1 ${isEtapeActive(colis, etape.key) ? "bg-success" : "bg-secondary"}`}
                                                            style={{ opacity: isEtapeActive(colis, etape.key) ? 1 : 0.5 }}
                                                        >
                                                            {etape.label}
                                                            {isEtapeActive(colis, etape.key) && colis[`date_${etape.key}`] ? (
                                                                <span className="ms-2 small">{new Date(colis[`date_${etape.key}`]).toLocaleDateString()}</span>
                                                            ) : null}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {colis.statut !== "recu" && (
                                                    <button className="btn btn-outline-primary btn-sm" onClick={() => openModalForStatut(colis)}>
                                                        ➕ Étape suivante
                                                    </button>
                                                )}
                                                {renderPreuves(colis)}
                                                <button className="btn btn-outline-secondary btn-sm" onClick={() => imprimerRecu(colis)}>
                                                    🖨️ Imprimer
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => openEditModal(colis)}
                                                >
                                                    ✏️ Modifier
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => {
                                                        setColisASupprimer(colis);
                                                        const modal = new window.bootstrap.Modal(document.getElementById("confirmDeleteModal"));
                                                        modal.show();
                                                    }}
                                                >
                                                    🗑️ Supprimer
                                                </button>

                                                {colis.imageUrls?.length > 0 && (
                                                    <button
                                                        className="btn btn-outline-dark btn-sm"
                                                        onClick={() => {
                                                            setSlideImage(colis.imageUrls);
                                                            const modal = new window.bootstrap.Modal(document.getElementById("slideImageModal"));
                                                            modal.show();
                                                        }}
                                                    >
                                                        🔍 Voir image
                                                    </button>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Modal pour preuve */}
                    <div className="modal fade" id="preuveModal" tabIndex="-1" aria-labelledby="preuveModalLabel" aria-hidden="true">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="preuveModalLabel">Uploader une preuve pour l'étape : {nextStep}</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                </div>
                                <div className="modal-body">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="form-control mb-2"
                                        onChange={handleFileChange}
                                    />
                                    <label className="form-label">Date de l'étape</label>
                                    <input
                                        type="date"
                                        className="form-control mb-2"
                                        value={stepDate}
                                        onChange={e => setStepDate(e.target.value)}
                                    />
                                    <label className="form-label">Nombre d'articles livrés à cette étape</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={selectedColis?.nombreArticles || 1}
                                        className="form-control"
                                        value={stepArticles}
                                        onChange={e => setStepArticles(e.target.value)}
                                        required
                                    />
                                </div>
                                {/* <div className="modal-body">
                                    <input type="file" accept="image/*" className="form-control" onChange={(e) => setFile(e.target.files[0])} />
                                </div> */}
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
                                    <button type="button" className="btn btn-primary" onClick={handleUploadPreuve}>Valider</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Modal slide image */}
                    <div className="modal fade" id="slideImageModal" tabIndex="-1" aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered modal-fullscreen-sm-down modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">📸 Aperçu des images du colis</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
                                </div>
                                <div className="modal-body">
                                    {Array.isArray(slideImage) && slideImage.length > 0 ? (
                                        <div className="row g-3">
                                            {slideImage.map((imgUrl, index) => (
                                                <div className="col-12 col-sm-6 text-center" key={index}>
                                                    <img
                                                        src={imgUrl}
                                                        alt={`Colis ${index + 1}`}
                                                        className="img-fluid rounded shadow"
                                                        style={{ maxHeight: "400px", objectFit: "contain" }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-center">Aucune image à afficher.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Modal de comfirmation de suppression */}
                    <div className="modal fade" id="confirmDeleteModal" tabIndex="-1" aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title text-danger">Confirmer la suppression</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
                                </div>
                                <div className="modal-body">
                                    <p>Voulez-vous vraiment supprimer le colis <strong>{colisASupprimer?.colisNom}</strong> ? Cette action est irréversible.</p>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
                                    <button className="btn btn-danger" onClick={confirmerSuppression}>Supprimer</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/*Modal d'édition de colis*/}
                    <div className="modal fade" id="editColisModal" tabIndex="-1" aria-hidden="true">
                        <div className="modal-dialog modal-lg modal-dialog-scrollable">
                            <form className="modal-content" onSubmit={async (e) => {
                                e.preventDefault();
                                await updateDoc(doc(db, "colis", editColis.id), editForm);
                                toast.success("Colis modifié !");
                                setEditColis(null);
                                const modal = window.bootstrap.Modal.getInstance(document.getElementById("editColisModal"));
                                modal.hide();
                            }}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Modifier le colis</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                                </div>
                                <div className="modal-body">
                                    <label>Nom du colis</label>
                                    <input type="text" className="form-control mb-2" value={editForm.colisNom || ""} onChange={e => setEditForm(f => ({ ...f, colisNom: e.target.value }))} required />

                                    <label>Nombre d'articles</label>
                                    <input type="number" className="form-control mb-2" value={editForm.nombreArticles || ""} onChange={e => setEditForm(f => ({ ...f, nombreArticles: e.target.value }))} required />

                                    <label>Date d'achat</label>
                                    <input type="date" className="form-control mb-2" value={editForm.dateAchat || ""} onChange={e => setEditForm(f => ({ ...f, dateAchat: e.target.value }))} required />

                                    <label>Montant d'achat</label>
                                    <input type="number" className="form-control mb-2" value={editForm.montantAchat || ""} onChange={e => setEditForm(f => ({ ...f, montantAchat: e.target.value }))} required />

                                    <label>Montant shipping</label>
                                    <input type="number" className="form-control mb-2" value={editForm.montantShipping || ""} onChange={e => setEditForm(f => ({ ...f, montantShipping: e.target.value }))} />

                                    <label>Dépôt effectué</label>
                                    <input type="number" className="form-control mb-2" value={editForm.montantDepot || ""} onChange={e => setEditForm(f => ({ ...f, montantDepot: e.target.value }))} />

                                    <label>Reste à payer</label>
                                    <input type="number" className="form-control mb-2" value={editForm.montantReste || ""} onChange={e => setEditForm(f => ({ ...f, montantReste: e.target.value }))} readOnly />

                                    <label>Nom de l'envoyeur</label>
                                    <input type="text" className="form-control mb-2" value={editForm.envoyeurNom || ""} onChange={e => setEditForm(f => ({ ...f, envoyeurNom: e.target.value }))} required />

                                    <label>Tél de l'envoyeur</label>
                                    <input type="tel" className="form-control mb-2" value={editForm.envoyeurTel || ""} onChange={e => setEditForm(f => ({ ...f, envoyeurTel: e.target.value }))} required />

                                    <label>Adresse de l'envoyeur</label>
                                    <input type="text" className="form-control mb-2" value={editForm.envoyeurAdresse || ""} onChange={e => setEditForm(f => ({ ...f, envoyeurAdresse: e.target.value }))} />

                                    <label>Nom du récepteur</label>
                                    <input type="text" className="form-control mb-2" value={editForm.recepteurNom || ""} onChange={e => setEditForm(f => ({ ...f, recepteurNom: e.target.value }))} required />

                                    <label>Tél du récepteur</label>
                                    <input type="tel" className="form-control mb-2" value={editForm.recepteurTel || ""} onChange={e => setEditForm(f => ({ ...f, recepteurTel: e.target.value }))} required />

                                    <label>Adresse du récepteur</label>
                                    <input type="text" className="form-control mb-2" value={editForm.recepteurAdresse || ""} onChange={e => setEditForm(f => ({ ...f, recepteurAdresse: e.target.value }))} />
                                </div>
                                <label>Images du colis</label>
                                <div className="mb-2 d-flex flex-wrap gap-2">
                                    {Array.isArray(editForm.imageUrls) && editForm.imageUrls.length > 0 ? (
                                        editForm.imageUrls.map((img, i) => (
                                            <div key={i} style={{ position: "relative", display: "inline-block" }}>
                                                <img src={img} alt={`img-${i}`} width={60} className="img-thumbnail" />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                                    style={{ width: 24, height: 24, borderRadius: "50%", padding: 0 }}
                                                    title="Supprimer"
                                                    onClick={() => setEditForm(f => ({
                                                        ...f,
                                                        imageUrls: f.imageUrls.filter((_, idx) => idx !== i)
                                                    }))}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <span className="text-muted">Aucune image</span>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="form-control mb-2"
                                    onChange={e => {
                                        const files = Array.from(e.target.files);
                                        // Optionnel : upload vers Firebase Storage ici et récupérer les URLs
                                        // Pour l’exemple, on ajoute les fichiers localement
                                        setEditForm(f => ({
                                            ...f,
                                            imageUrls: [
                                                ...(f.imageUrls || []),
                                                ...files.map(file => URL.createObjectURL(file))
                                            ]
                                        }));
                                    }}
                                />
                                <small className="text-muted">Les images ajoutées remplaceront celles existantes après validation.</small>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
                                    <button type="submit" className="btn btn-primary">Enregistrer</button>
                                </div>
                            </form>
                        </div>
                    </div>


                </div>
            </div>
        </div>

    );

}